<template>
  <main class="container-1">

    <div class="container-header">
      <Breadcrumb :links="routesBread" />
      <!-- <h2>Crear notificación</h2> -->
      <TabNav :links="routesNav" />
    </div>

    <div class="container-body">

      <div
        v-if="loading"
        class="message-box message-info"
      >
        Cargando usuarios...
      </div>

      <form
        v-else
        class="notification-form"
        @submit.prevent="requestCreation"
      >
        <!-- Título -->
        <div class="form-field">
          <label for="title">
            Título
          </label>

          <input
            id="title"
            v-model.trim="form.title"
            type="text"
            maxlength="120"
            placeholder="Ingresa el título"
          />
        </div>

        <!-- Mensaje -->
        <div class="form-field">
          <label for="message">
            Mensaje
          </label>

          <textarea
            id="message"
            v-model.trim="form.message"
            rows="5"
            maxlength="1000"
            placeholder="Escribe el mensaje de la notificación"
          ></textarea>

          <span class="field-help">
            {{ form.message.length }}/1000 caracteres
          </span>
        </div>

        <div class="form-grid">

          <!-- Expiración -->
          <div class="form-field">
            <label for="expires-at">
              Fecha de expiración
            </label>

            <input
              id="expires-at"
              v-model="form.expires_at"
              type="date"
              :min="form.active ? minimumExpirationDate : null"
            />

            <span class="field-help">
              {{ form.active
                ? 'Déjala vacía si la notificación activa no debe expirar.'
                : 'La fecha no se valida mientras esté inactiva en el sistema.'
              }}
            </span>
          </div>

          <!-- Opciones -->
          <div class="form-field">
            <span class="field-label">
              Opciones
            </span>

            <label class="checkbox-option">
              <input
                v-model="form.send_email"
                type="checkbox"
              />

              <span>
                Enviar por correo al crear
              </span>
            </label>

            <label class="checkbox-option">
              <input
                v-model="form.active"
                type="checkbox"
              />

              <span>
                Notificación activa en el sistema
              </span>
            </label>
          </div>

        </div>

        <!-- Destinatarios -->
        <section class="recipients-section">

          <div class="section-header">
            <div>
              <h3>Destinatarios</h3>

              <p>
                Selecciona todos los usuarios o personas específicas
                organizadas por grupo.
              </p>
            </div>

            <span class="selected-count">
              {{ selectedUsersCount }}
              {{ selectedUsersCount === 1
                ? 'destinatario'
                : 'destinatarios'
              }}
            </span>
          </div>

          <!-- Tipo de audiencia -->
          <div class="audience-options">

            <label
              class="audience-option"
              :class="{ selected: form.all_users }"
            >
              <input
                v-model="form.all_users"
                type="radio"
                :value="true"
              />

              <span>
                <strong>Todos los usuarios</strong>

                <small>
                  La notificación estará disponible para todos los
                  usuarios registrados.
                </small>
              </span>
            </label>

            <label
              class="audience-option"
              :class="{ selected: !form.all_users }"
            >
              <input
                v-model="form.all_users"
                type="radio"
                :value="false"
              />

              <span>
                <strong>Usuarios específicos</strong>

                <small>
                  Selecciona personas desde uno o varios grupos.
                </small>
              </span>
            </label>

          </div>

          <!-- Usuarios agrupados -->
          <div
            v-if="!form.all_users"
            class="groups-panel"
          >
            <div class="groups-panel-toolbar">
              <div>
                <strong>Grupos del sistema</strong>

                <span>
                  {{ groupsWithUsers.length }}
                  {{ groupsWithUsers.length === 1
                    ? 'grupo disponible'
                    : 'grupos disponibles'
                  }}
                </span>
              </div>

              <button
                type="button"
                class="groups-toggle-all"
                @click="toggleAllGroups"
              >
                {{ allGroupsExpanded
                  ? 'Contraer todos'
                  : 'Expandir todos'
                }}
              </button>
            </div>

            <div class="groups-container">

              <article
                v-for="group in groupsWithUsers"
                :key="group.id"
                class="group-card"
                :class="{
                  'group-card-expanded': isGroupExpanded(group.id)
                }"
              >
                <header class="group-header">

                  <label
                    class="group-selector"
                    @click.stop
                  >
                    <input
                      type="checkbox"
                      :checked="isEntireGroupSelected(group)"
                      :indeterminate.prop="
                        isGroupPartiallySelected(group)
                      "
                      :disabled="!group.users.length"
                      @change="
                        toggleGroup(
                          group,
                          $event.target.checked
                        )
                      "
                    />

                    <span>
                      {{ group.name }}
                    </span>
                  </label>

                  <div class="group-header-actions">

                    <span class="group-count">
                      {{ group.users.length }}
                      {{ group.users.length === 1
                        ? 'usuario'
                        : 'usuarios'
                      }}
                    </span>

                    <button
                      type="button"
                      class="group-collapse-button"
                      :aria-expanded="
                        isGroupExpanded(group.id)
                      "
                      :aria-label="
                        isGroupExpanded(group.id)
                          ? `Ocultar usuarios de ${group.name}`
                          : `Mostrar usuarios de ${group.name}`
                      "
                      @click="
                        toggleGroupVisibility(group.id)
                      "
                    >
                      <span class="collapse-button-text">
                        {{ isGroupExpanded(group.id)
                          ? 'Ocultar'
                          : 'Mostrar'
                        }}
                      </span>

                      <span
                        class="group-chevron"
                        :class="{
                          expanded: isGroupExpanded(group.id)
                        }"
                      >
                        ▾
                      </span>
                    </button>

                  </div>

                </header>

                <!-- Solo se muestran los usuarios si el grupo está abierto -->
                <div
                  v-show="isGroupExpanded(group.id)"
                  class="group-content"
                >
                  <div
                    v-if="group.users.length"
                    class="users-grid"
                  >
                    <label
                      v-for="user in group.users"
                      :key="user.id"
                      class="user-option"
                      :class="{
                        selected: isUserSelected(user.id)
                      }"
                    >
                      <input
                        type="checkbox"
                        :checked="isUserSelected(user.id)"
                        @change="
                          toggleUser(
                            user.id,
                            $event.target.checked
                          )
                        "
                      />

                      <span>
                        <strong>
                          {{ user.firstname }}
                          {{ user.lastname }}
                        </strong>

                        <small>
                          {{ user.username || 'Sin correo registrado' }}
                        </small>
                      </span>
                    </label>
                  </div>

                  <div
                    v-else
                    class="empty-group"
                  >
                    Este grupo no tiene usuarios activos.
                  </div>
                </div>

              </article>

              <div
                v-if="groupsWithUsers.length === 0"
                class="empty-groups"
              >
                No se encontraron grupos asociados al sistema.
              </div>

            </div>
          </div>

        </section>

        <!-- Mensajes -->
        <div
          v-if="errorMessage"
          class="message-box message-error"
        >
          {{ errorMessage }}
        </div>

        <div
          v-if="successMessage"
          class="message-box message-success"
        >
          {{ successMessage }}
        </div>

        <!-- Acciones -->
        <div class="form-actions">

          <router-link
            to="/usuarios/notificaciones"
            class="form-button button-secondary"
          >
            Volver
          </router-link>

          <button
            type="submit"
            class="form-button button-primary"
            :disabled="saving"
          >
            {{ saving
              ? 'Creando...'
              : 'Crear notificación'
            }}
          </button>

        </div>

      </form>

      <NotificationConfirmationModal
        v-if="showConfirmation"
        heading="Confirmar creación"
        :notification-title="form.title"
        :active-in-system="form.active"
        :email-will-be-sent="form.send_email"
        email-label="Envío por correo"
        :recipient-count="selectedUsersCount"
        confirm-label="Sí, crear notificación"
        loading-label="Creando notificación..."
        :loading="saving"
        @cancel="closeConfirmation"
        @confirm="confirmCreation"
      />

    </div>

    <!-- <SideNav
      class="container-sidenav"
      :links="routesNav"
    /> -->

  </main>
</template>

<script>
import SideNav from '@/components/SideNav.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import TabNav from '@/components/TabNav.vue'
import NotificationConfirmationModal from '@/components/common/NotificationConfirmationModal.vue'
import { restNotification } from '@/components/mixin/restNotification.js'
import { restGroup } from '@/components/mixin/restGroup.js'
export default {
  name: 'CreateNotification',
    mixins: [
    restNotification,
    restGroup
  ],
  components: {
    SideNav,
    Breadcrumb,
    TabNav,
    NotificationConfirmationModal
  },

  data() {
    return {
      loading: true,
      saving: false,

      errorMessage: '',
      successMessage: '',
      showConfirmation: false,

      groupsWithUsers: [],
      expandedGroups: {},
      form: {
        title: '',
        message: '',
        user_ids: [],
        all_users: false,
        send_email: false,
        active: true,
        expires_at: ''
      }
    }
  },

  computed: {
    allGroupsExpanded() {
      if (this.groupsWithUsers.length === 0) {
        return false
      }

      return this.groupsWithUsers.every(
        (group) => this.isGroupExpanded(group.id)
      )
    },
    routesNav() {
      return []
    },

    routesBread() {
      return [
        {
          name: 'Usuarios',
          path: '/usuarios'
        },
        {
          name: 'Notificaciones',
          path: '/usuarios/notificaciones'
        },
        {
          name: 'Crear notificación'
        }
      ]
    },

    totalUsers() {
      return this.groupsWithUsers.reduce(
        (total, group) => {
          return total + (group.users || []).length
        },
        0
      )
    },

    selectedUsersCount() {
      if (this.form.all_users) {
        return this.totalUsers
      }

      return this.form.user_ids.length
    },

    minimumExpirationDate() {
      const date = new Date()

      date.setDate(date.getDate() + 1)

      return this.formatDateForInput(date)
    }
  },

  created() {
    if (!this.$store.getters.getPermissions.users) {
      this.$router.push('/pagina-no-encontrada')
    }
  },

  mounted() {
    this.loadGroupsWithUsers()
  },

  methods: {
    isGroupExpanded(groupID) {
    return this.expandedGroups[groupID] === true
  },
  toggleGroupVisibility(groupID) {
    this.$set(
      this.expandedGroups,
      groupID,
      !this.isGroupExpanded(groupID)
    )
  },

  toggleAllGroups() {
    const shouldExpand = !this.allGroupsExpanded

    this.groupsWithUsers.forEach((group) => {
      this.$set(
        this.expandedGroups,
        group.id,
        shouldExpand
      )
    })
  },

  async loadGroupsWithUsers() {
    this.loading = true
    this.errorMessage = ''

    try {
      const response = await this.getGroupsWithUsers()

      this.groupsWithUsers = (
        response.data || []
      ).map((group) => ({
        ...group,
        users: group.users || []
      }))

      this.expandedGroups = {}
    } catch (error) {
      console.error(error)

      this.groupsWithUsers = []

      this.errorMessage =
        error.response?.data?.message ||
        'No fue posible cargar los grupos y usuarios.'
    } finally {
      this.loading = false
    }
  },
    formatDateForInput(date) {
      const year = date.getFullYear()

      const month = String(
        date.getMonth() + 1
      ).padStart(2, '0')

      const day = String(
        date.getDate()
      ).padStart(2, '0')

      return `${year}-${month}-${day}`
    },

    expirationToISO() {
      if (!this.form.expires_at) {
        return null
      }

      const expirationDate = new Date(
        `${this.form.expires_at}T23:59:59`
      )

      return expirationDate.toISOString()
    },

    validateForm() {
      if (!this.form.title) {
        return 'El título es obligatorio.'
      }

      if (this.form.title.length > 120) {
        return 'El título no puede superar los 120 caracteres.'
      }

      if (!this.form.message) {
        return 'El mensaje es obligatorio.'
      }

      if (this.form.message.length > 1000) {
        return 'El mensaje no puede superar los 1000 caracteres.'
      }

      if (
        !this.form.all_users &&
        this.form.user_ids.length === 0
      ) {
        return 'Debes seleccionar al menos un usuario.'
      }

      if (
        this.form.active &&
        this.form.expires_at
      ) {
        const expiration = new Date(
          `${this.form.expires_at}T23:59:59`
        )

        if (expiration <= new Date()) {
          return 'La fecha de expiración debe ser futura.'
        }
      }

      return ''
    },

    requestCreation() {
      if (this.saving) return

      this.errorMessage = ''
      this.successMessage = ''

      const validationMessage =
        this.validateForm()

      if (validationMessage) {
        this.errorMessage =
          validationMessage

        return
      }

      this.showConfirmation = true
    },

    closeConfirmation() {
      if (!this.saving) {
        this.showConfirmation = false
      }
    },

    confirmCreation() {
      if (this.saving) return

      this.showConfirmation = false
      return this.createNotification()
    },

    async createNotification() {
      if (this.saving) return

      const payload = {
        title: this.form.title,
        message: this.form.message,

        user_ids: this.form.all_users
          ? []
          : [...this.form.user_ids],

        all_users: this.form.all_users,
        send_email: this.form.send_email,
        active: this.form.active,
        expires_at: this.expirationToISO()
      }

      this.saving = true

      try {
        await this.postNotification(payload)

        this.successMessage =
          'Notificación creada exitosamente.'

        setTimeout(() => {
          this.$router.push(
            '/usuarios/notificaciones'
          )
        }, 700)
      } catch (error) {
        console.error(error)

        this.errorMessage =
          error.response &&
          error.response.data
            ? error.response.data.message
            : 'No fue posible crear la notificación.'
      } finally {
        this.saving = false
      }
    },

    isUserSelected(userID) {
      return this.form.user_ids.includes(userID)
    },

    toggleUser(userID, checked) {
      if (checked) {
        if (!this.form.user_ids.includes(userID)) {
          this.form.user_ids.push(userID)
        }

        return
      }

      this.form.user_ids = this.form.user_ids.filter(
        (id) => id !== userID
      )
    },

    groupSelectedUsers(group) {
      const users = group.users || []

      return users.filter((user) =>
        this.form.user_ids.includes(user.id)
      )
    },

    isEntireGroupSelected(group) {
      const users = group.users || []

      if (users.length === 0) {
        return false
      }

      return users.every((user) =>
        this.form.user_ids.includes(user.id)
      )
    },

    isGroupPartiallySelected(group) {
      const users = group.users || []

      if (users.length === 0) {
        return false
      }

      const selectedUsers =
        this.groupSelectedUsers(group).length

      return (
        selectedUsers > 0 &&
        selectedUsers < users.length
      )
    },

    toggleGroup(group, checked) {
      const userIDs = (group.users || []).map(
        (user) => user.id
      )

      if (checked) {
        this.form.user_ids = Array.from(
          new Set([
            ...this.form.user_ids,
            ...userIDs
          ])
        )

        return
      }

      this.form.user_ids = this.form.user_ids.filter(
        (id) => !userIDs.includes(id)
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.notification-form {
  max-width: 900px;
}

.form-field {
  display: flex;
  flex-direction: column;
  margin-bottom: 1em;
}

.form-field label,
.field-label {
  margin-bottom: 0.35em;
  color: var(--color-navy);
  font-size: 0.84em;
  font-weight: 700;
}

.form-field input[type='text'],
.form-field input[type='date'],
.form-field textarea {
  width: 100%;
  padding: 0.55em 0.7em;
  border: 1px solid var(--color-ghost);
  border-radius: 4px;
  background: var(--color-white);
  color: var(--color-graphite);
  font-family: inherit;
  font-size: 0.9em;
}

.form-field textarea {
  resize: vertical;
}

.form-field input:focus,
.form-field textarea:focus {
  outline: 2px solid var(--color-steel);
  outline-offset: 1px;
  border-color: var(--color-steel);
}

.field-help {
  margin-top: 0.35em;
  color: var(--color-ghost);
  font-size: 0.75em;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5em;
}

.checkbox-option {
  display: flex;
  align-items: center;
  gap: 0.55em;
  margin-bottom: 0.7em;
  color: var(--color-graphite);
  font-size: 0.85em;
  cursor: pointer;
}

.checkbox-option input,
.audience-option input,
.group-selector input,
.user-option input {
  accent-color: var(--color-steel);
}

.recipients-section {
  margin-top: 1.5em;
  padding-top: 1.3em;
  border-top: 1px solid var(--color-fog);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1em;
  margin-bottom: 1em;
}

.section-header h3 {
  margin: 0;
  color: var(--color-navy);
  font-size: 1.05em;
}

.section-header p {
  margin: 0.25em 0 0;
  color: var(--color-ghost);
  font-size: 0.8em;
}

.selected-count {
  padding: 0.35em 0.7em;
  border: 1px solid var(--color-fog);
  border-radius: 4px;
  color: var(--color-steel);
  font-size: 0.78em;
  font-weight: 600;
  white-space: nowrap;
}

.audience-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.8em;
}

.audience-option {
  display: flex;
  align-items: flex-start;
  gap: 0.7em;
  padding: 0.9em;
  border: 1px solid var(--color-fog);
  border-radius: 4px;
  background: var(--color-white);
  cursor: pointer;
}

.audience-option.selected {
  border-color: var(--color-steel);
  background: var(--color-mist);
}

.audience-option strong,
.audience-option small {
  display: block;
}

.audience-option strong {
  color: var(--color-navy);
  font-size: 0.85em;
}

.audience-option small {
  margin-top: 0.25em;
  color: var(--color-ghost);
  font-size: 0.75em;
}

.groups-panel {
  margin-top: 1em;
  overflow: hidden;
  border: 1px solid var(--color-fog);
  border-radius: 6px;
  background: var(--color-white);
}

.groups-panel-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1em;
  padding: 0.75em 0.9em;
  border-bottom: 1px solid var(--color-fog);
  background: var(--color-mist);
}

.groups-panel-toolbar strong {
  display: block;
  color: var(--color-navy);
  font-size: 0.85em;
}

.groups-panel-toolbar span {
  display: block;
  margin-top: 0.15em;
  color: var(--color-ghost);
  font-size: 0.72em;
}

.groups-toggle-all {
  padding: 0.4em 0.75em;
  border: 1px solid var(--color-steel);
  border-radius: 4px;
  background: var(--color-white);
  color: var(--color-steel);
  font-family: inherit;
  font-size: 0.75em;
  font-weight: 600;
  cursor: pointer;
}

.groups-toggle-all:hover {
  background: var(--color-steel);
  color: var(--color-white);
}

.groups-container {
  display: grid;
  gap: 0.75em;

  max-height: 360px;
  padding: 0.8em;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.group-card {
  overflow: hidden;
  border: 1px solid var(--color-fog);
  border-radius: 4px;
  background: var(--color-white);
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1em;
  padding: 0.75em 0.9em;
  background: var(--color-mist);
}

/*
 * La línea inferior solamente aparece cuando
 * el grupo se encuentra desplegado.
 */
.group-card-expanded .group-header {
  border-bottom: 1px solid var(--color-fog);
}

.group-header-actions {
  display: flex;
  align-items: center;
  gap: 0.8em;
}

.group-collapse-button {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  padding: 0.25em 0.4em;
  border: none;
  background: transparent;
  color: var(--color-steel);
  font-family: inherit;
  font-size: 0.75em;
  font-weight: 600;
  cursor: pointer;
}

.group-collapse-button:hover {
  color: var(--color-navy);
}

.group-chevron {
  display: inline-block;
  font-size: 1.25em;
  line-height: 1;
  transition: transform 0.2s ease;
}

.group-chevron.expanded {
  transform: rotate(180deg);
}

.group-content {
  background: var(--color-white);
}

.group-selector {
  display: flex;
  align-items: center;
  gap: 0.55em;
  color: var(--color-navy);
  font-size: 0.85em;
  font-weight: 700;
  cursor: pointer;
}

.group-selector input:disabled + span {
  color: var(--color-ghost);
  cursor: not-allowed;
}

.group-count {
  color: var(--color-steel);
  font-size: 0.75em;
  font-weight: 600;
}

.users-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}

.user-option {
  display: flex;
  align-items: flex-start;
  gap: 0.55em;
  padding: 0.75em 0.9em;
  border-bottom: 1px solid var(--color-fog);
  cursor: pointer;
}

.user-option:nth-child(odd) {
  border-right: 1px solid var(--color-fog);
}

.user-option.selected {
  background: var(--color-mist);
}

.user-option strong,
.user-option small {
  display: block;
}

.user-option strong {
  color: var(--color-graphite);
  font-size: 0.82em;
}

.user-option small {
  margin-top: 0.15em;
  color: var(--color-ghost);
  font-size: 0.73em;
}

.empty-group,
.empty-groups {
  padding: 1em;
  color: var(--color-ghost);
  font-size: 0.82em;
}

.empty-groups {
  border: 1px solid var(--color-fog);
  border-radius: 4px;
  text-align: center;
}

.form-actions {
  display: flex;
  gap: 0.7em;
  margin-top: 1.4em;
}

.form-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 35px;
  padding: 0.5em 1.1em;
  border: 1px solid;
  border-radius: 12px;
  font-family: inherit;
  font-size: 0.82em;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

.button-secondary {
  border-color: var(--color-steel);
  background: var(--color-white);
  color: var(--color-steel);
}

.button-secondary:hover {
  background: var(--color-mist);
  color: var(--color-navy);
}

.button-primary {
  border-color: var(--color-steel);
  background: var(--color-white);
  color: var(--color-blue);
}

.button-primary:hover {
  background: var(--color-steel);
  color: var(--color-white);
}

.button-primary:disabled {
  border-color: var(--color-fog);
  background: var(--color-mist);
  color: var(--color-ghost);
  cursor: not-allowed;
}

.message-box {
  max-width: 900px;
  margin-top: 1em;
  padding: 0.9em 1em;
  border: 1px solid;
  border-radius: 4px;
  font-size: 0.85em;
}

.message-info {
  border-color: var(--color-steel);
  background: var(--color-mist);
  color: var(--color-steel);
}

.message-error {
  border-color: var(--color-danger);
  background: var(--color-mist);
  color: var(--color-danger);
}

.message-success {
  border-color: var(--color-success);
  background: var(--color-mist);
  color: var(--color-success);
}

@media (max-width: 760px) {
  .form-grid,
  .audience-options,
  .users-grid {
    grid-template-columns: 1fr;
  }

  .user-option:nth-child(odd) {
    border-right: none;
  }

  .section-header {
    flex-direction: column;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .form-button {
    width: 100%;
  }
  .groups-container {
  max-height: 300px;
}

.groups-panel-toolbar {
  align-items: flex-start;
}

.group-header {
  align-items: flex-start;
}

.group-header-actions {
  flex-direction: column;
  align-items: flex-end;
  gap: 0.3em;
}

.collapse-button-text {
  display: none;
}
}
</style>
